import { Box, InputLeftElement, InputRightElement, Spinner, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

import { extractErrorMessage } from "@equidefi/portals";
import GoogleClient from "@equidefi/portals/clients/GoogleClient";
import { useGoogleAddressSearch } from "@equidefi/portals/hooks/useAddressSearch";
import { useApi } from "@equidefi/portals/hooks/useApi";
import { Input } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import useDebounce from "@/hooks/use-debounce";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Street address input with inline Google Places autocomplete.
 *
 * Behaves like a regular controlled <Input> for the Street 1 field — typing
 * updates the form value via onChange. While the user types, autocomplete
 * suggestions render in a dropdown beneath the input. Selecting a suggestion
 * fires onSelected with the resolved address details (the parent is
 * responsible for populating the remaining address fields). After a
 * selection, subsequent edits that differ from the resolved value reopen
 * suggestions automatically (e.g. select-all + type, double-click + type).
 * Clearing the field also resets the resolved state.
 */
export const StreetAutocompleteInput = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  onSelected,
  isRequired = false,
  isInvalid = false,
  isDisabled = false,
  error,
  placeholder = "Start typing your street address",
  mb = "2",
}) => {
  const containerRef = useRef(null);
  const isMountedRef = useRef(true);
  const resolvedRef = useRef(false);
  const resolvedValueRef = useRef("");
  const [isOpen, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isResolving, setIsResolving] = useState(false);
  const toast = useToast();

  const debouncedSearch = useDebounce(value ?? "", SEARCH_DEBOUNCE_MS);
  const { data: options = [], isFetching } = useGoogleAddressSearch(debouncedSearch, {
    enabled: isOpen && !!debouncedSearch && !isDisabled,
    onError: (err) => {
      toast({
        status: "error",
        description: extractErrorMessage(err, "Sorry, could not load addresses"),
      });
    },
  });

  const googleApi = useApi(GoogleClient);

  useEffect(() => {
    isMountedRef.current = true;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      isMountedRef.current = false;
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (event) => {
    const newValue = event.target.value;
    onChange?.(event);
    setActiveIndex(-1);

    if (!newValue) {
      resolvedRef.current = false;
      resolvedValueRef.current = "";
      setOpen(false);
      return;
    }

    // If the user overwrote the resolved value (e.g. select-all + type,
    // double-click + type), treat it as a new search instead of a correction.
    if (resolvedValueRef.current.length > 0 && newValue !== resolvedValueRef.current) {
      resolvedRef.current = false;
      resolvedValueRef.current = "";
    }

    // After a selection, treat further edits as manual corrections rather
    // than a new search. Clearing the field (or overwriting it) resets this.
    if (resolvedValueRef.current.length > 0) return;

    setOpen(true);
  };

  const selectOption = async (option) => {
    if (!option) return;
    setOpen(false);
    setActiveIndex(-1);
    setIsResolving(true);
    try {
      const data = await googleApi.place(option.value);
      if (!isMountedRef.current) return;
      resolvedRef.current = true;
      resolvedValueRef.current = data.street ?? "";
      onSelected?.(data);
    } catch (err) {
      console.error("Failed to load place details", err);
      if (isMountedRef.current) {
        toast({
          status: "error",
          description: extractErrorMessage(err, "Sorry, could not load address details"),
        });
      }
    } finally {
      if (isMountedRef.current) setIsResolving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen || options.length === 0) {
      if (event.key === "Escape") setOpen(false);
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((idx) => (idx + 1) % options.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((idx) => (idx <= 0 ? options.length - 1 : idx - 1));
        break;
      case "Enter":
        if (activeIndex >= 0) {
          event.preventDefault();
          selectOption(options[activeIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
    }
  };

  const showSpinner = isFetching || isResolving;
  const showDropdown = isOpen && !isDisabled && (isFetching || options.length > 0);
  const listboxId = `${name}-listbox`;

  return (
    <Box position="relative" ref={containerRef} mb={mb}>
      <Input
        name={name}
        label={label}
        value={value ?? ""}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        isRequired={isRequired}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        error={error}
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={showDropdown ? listboxId : undefined}
        aria-activedescendant={showDropdown && activeIndex >= 0 ? `${name}-option-${activeIndex}` : undefined}
        mb="0"
        inputLeftElement={
          <InputLeftElement pointerEvents="none" color="gray.400">
            <Icon.MapPin size="1.1em" />
          </InputLeftElement>
        }
        inputRightElement={
          showSpinner ? (
            <InputRightElement>
              <Spinner size="sm" color="gray.400" />
            </InputRightElement>
          ) : null
        }
      />

      {showDropdown && (
        <Box
          id={listboxId}
          // biome-ignore lint/a11y/useSemanticElements: custom combobox listbox cannot use native <select>
          role="listbox"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          zIndex={10}
          maxH="240px"
          overflowY="auto">
          {options.length === 0 && isFetching ? (
            <Box px={3} py={2} color="gray.500" fontSize="sm">
              Searching…
            </Box>
          ) : (
            options.map((option, index) => (
              <Box
                key={option.value}
                id={`${name}-option-${index}`}
                // biome-ignore lint/a11y/useSemanticElements: combobox option cannot use native <option>
                role="option"
                aria-selected={index === activeIndex}
                px={3}
                py={2}
                cursor="pointer"
                bg={index === activeIndex ? "gray.100" : "white"}
                _hover={{ bg: "gray.100" }}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(option)}>
                {option.label}
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};